class CustomSubscriptionWidget extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.abortController = new AbortController();
    this.cacheElements();
    this.restoreState();
    this.render();
    this.syncSellingPlanInput();
    requestAnimationFrame(() => {
      if (this.isConnected) {
        this.render();
        this.syncSellingPlanInput();
      }
    });

    this.addEventListener('change', this.handleChange);
    this.addEventListener('click', this.handleClick);
    document.addEventListener('click', this.handleDocumentClick, { signal: this.abortController.signal });

    const eventTarget = this.closest('.shopify-section, dialog, product-card') || document;
    eventTarget.addEventListener('variant:update', this.handleVariantUpdate, { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('click', this.handleClick);
  }

  cacheElements() {
    this.planOptions = Array.from(this.querySelectorAll('[data-plan-option]'));
    this.frequency = this.querySelector('[data-subscription-frequency]');
    this.summaryLabel = this.querySelector('[data-subscription-summary-label]');
    this.badge = this.querySelector('[data-subscription-badge]');
    this.price = this.querySelector('[data-subscription-price]');
    this.comparePrice = this.querySelector('[data-subscription-compare-price]');
  }

  restoreState() {
    const desiredPurchaseType = this.dataset.initialPurchaseType || this.dataset.selectedPurchaseType || 'onetime';
    const purchaseInput = this.querySelector(`[data-purchase-input="${desiredPurchaseType}"]`);

    if (purchaseInput) {
      purchaseInput.checked = true;
    }

    const desiredPlanId = this.dataset.initialPlanId;
    if (desiredPlanId) {
      const matchingOption = this.planOptions.find((option) => option.dataset.planId === desiredPlanId);
      if (matchingOption) {
        this.planOptions.forEach((option) => option.removeAttribute('data-selected'));
        matchingOption.dataset.selected = 'true';
      }
    }

    if (!this.getSelectedPlanOption() && this.planOptions[0]) {
      this.planOptions[0].dataset.selected = 'true';
    }
  }

  getSelectedPurchaseType() {
    return this.querySelector('[data-purchase-input]:checked')?.value || 'onetime';
  }

  getSelectedPlanOption() {
    return this.planOptions.find((option) => option.dataset.selected === 'true') || this.planOptions[0] || null;
  }

  setPurchaseType(purchaseType) {
    const purchaseInput = this.querySelector(`[data-purchase-input="${purchaseType}"]`);
    if (!purchaseInput) return false;

    const didChange = !purchaseInput.checked;
    purchaseInput.checked = true;

    this.render();
    this.syncSellingPlanInput();

    return didChange;
  }

  getProductForm() {
    return this.closest('.shopify-section')?.querySelector('product-form-component form[data-type="add-to-cart-form"]') || null;
  }

  render() {
    const purchaseType = this.getSelectedPurchaseType();
    const selectedPlan = this.getSelectedPlanOption();
    const oneTimePrice = this.dataset.oneTimePrice || '';
    const oneTimeComparePrice = this.dataset.oneTimeComparePrice || '';

    if (this.frequency) {
      this.frequency.dataset.disabled = String(purchaseType !== 'subscription');
    }

    if (purchaseType !== 'subscription') {
      this.frequency?.removeAttribute('open');
    }

    this.planOptions.forEach((option) => {
      option.dataset.selected = String(option === selectedPlan);
    });

    if (!selectedPlan) return;

    if (this.summaryLabel) {
      this.summaryLabel.textContent = selectedPlan.dataset.planName || '';
    }

    if (this.price) {
      this.price.textContent = selectedPlan.dataset.planPrice || '';
    }

    if (this.comparePrice) {
      const comparePrice =
        purchaseType === 'subscription' ? oneTimePrice : selectedPlan.dataset.planComparePrice || '';
      this.comparePrice.textContent = comparePrice;
      this.comparePrice.hidden = !comparePrice || comparePrice === selectedPlan.dataset.planPrice;
    }

    if (this.badge) {
      const discount = selectedPlan.dataset.planDiscount || '';
      this.badge.textContent = discount;
      this.badge.hidden = !discount;
    }

    this.updateDisplayedPrices({
      purchaseType,
      selectedPlan,
      oneTimePrice,
      oneTimeComparePrice,
    });
  }

  syncSellingPlanInput() {
    const form = this.getProductForm();
    if (!form) return;

    let input = form.querySelector('input[name="selling_plan"][data-custom-subscription-widget]');
    const purchaseType = this.getSelectedPurchaseType();
    const selectedPlan = this.getSelectedPlanOption();

    if (purchaseType === 'subscription' && selectedPlan?.dataset.planId) {
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        input.dataset.customSubscriptionWidget = 'true';
        form.appendChild(input);
      }

      input.value = selectedPlan.dataset.planId;
    } else if (input) {
      input.remove();
    }
  }

  updateDisplayedPrices({ purchaseType, selectedPlan, oneTimePrice, oneTimeComparePrice }) {
    const priceContainers = this.getPriceContainers();
    if (!priceContainers.length) return;

    let priceText = oneTimePrice;
    let compareText = oneTimeComparePrice;

    if (purchaseType === 'subscription' && selectedPlan?.dataset.planPrice) {
      priceText = selectedPlan.dataset.planPrice;
      compareText = oneTimePrice;
    }

    priceContainers.forEach((priceContainer) => {
      const priceNode = priceContainer.querySelector('.price');
      if (!priceNode) return;

      priceNode.textContent = priceText;
      this.syncAddToCartComparePrice(priceContainer, compareText);
    });
  }

  getPriceContainers() {
    const section = this.closest('.shopify-section');
    if (!section) return [];

    return [
      ...section.querySelectorAll('product-form-component [ref="priceContainer"]'),
      ...section.querySelectorAll('sticky-add-to-cart [ref="priceContainer"]'),
    ];
  }

  syncAddToCartComparePrice(priceContainer, compareText) {
    let compareGroup = priceContainer.querySelector('[data-subscription-atc-compare]');
    let compareNode = compareGroup?.querySelector('.compare-at-price') || priceContainer.querySelector('.compare-at-price');

    if (compareText) {
      if (!compareNode) {
        compareGroup = document.createElement('span');
        compareGroup.setAttribute('role', 'group');
        compareGroup.dataset.subscriptionAtcCompare = 'true';
        compareGroup.innerHTML =
          '<span class="visually-hidden">Regular price </span><span class="compare-at-price"></span>';
        priceContainer.insertBefore(compareGroup, priceContainer.firstChild);
        compareNode = compareGroup.querySelector('.compare-at-price');
      }

      compareNode.textContent = compareText;
      return;
    }

    if (compareGroup) {
      compareGroup.remove();
    } else if (compareNode) {
      const compareWrapper = compareNode.closest('[role="group"]');
      if (compareWrapper) {
        compareWrapper.remove();
      } else {
        compareNode.remove();
      }
    }
  }

  handleChange = (event) => {
    if (!(event.target instanceof Element)) return;

    const purchaseInput = event.target.closest('[data-purchase-input]');
    if (!purchaseInput) return;

    this.render();
    this.syncSellingPlanInput();
  };

  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const subscriptionTrigger = event.target.closest('[data-select-subscription]');
    const isSummaryTrigger = Boolean(subscriptionTrigger?.closest('summary'));

    if (subscriptionTrigger && !isSummaryTrigger) {
      this.setPurchaseType('subscription');
    }

    const selectedOption = event.target.closest('[data-plan-option]');
    if (!selectedOption) return;

    this.planOptions.forEach((option) => option.removeAttribute('data-selected'));
    selectedOption.dataset.selected = 'true';

    this.frequency?.removeAttribute('open');

    if (this.getSelectedPurchaseType() === 'subscription') {
      this.setPurchaseType('subscription');
      return;
    }

    this.render();
    this.syncSellingPlanInput();
  };

  handleDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;

    if (!this.contains(event.target)) {
      this.frequency?.removeAttribute('open');
    }
  };

  handleVariantUpdate = (event) => {
    const html = event.detail?.data?.html;
    const blockId = this.dataset.blockId;

    if (!html || !blockId) return;

    const replacement = html.querySelector(`[data-block-id="${blockId}"]`);
    if (!replacement) return;

    const nextNode = replacement.cloneNode(true);
    nextNode.dataset.initialPurchaseType = this.getSelectedPurchaseType();

    const selectedPlan = this.getSelectedPlanOption();
    if (selectedPlan?.dataset.planId) {
      nextNode.dataset.initialPlanId = selectedPlan.dataset.planId;
    }

    this.replaceWith(nextNode);
  };
}

if (!customElements.get('custom-subscription-widget')) {
  customElements.define('custom-subscription-widget', CustomSubscriptionWidget);
}
